import { X } from 'lucide-react'
import { useState } from 'react'
import Avatar from './ui/Avatar'
import api from '../services/api'
import { useToast } from '../contexts/ToastContext'

interface QuoteRepostModalProps {
  isOpen: boolean
  onClose: () => void
  post: {
    id: string
    author: {
      name: string
      username: string
      avatar?: string
      verified?: boolean
    }
    content: string
    mediaUrl?: string
    mediaType?: string
    onRecordCode?: string
  }
  currentUser?: {
    name?: string
    avatar?: string
  }
}

export default function QuoteRepostModal({
  isOpen,
  onClose,
  post,
  currentUser,
}: QuoteRepostModalProps) {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { showToast } = useToast()
  const maxLength = 280

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (submitting) return
    setSubmitting(true)

    try {
      const response = await api.createQuoteRepost({
        originalPostId: post.id,
        text: text.trim(),
      })

      if (response.success) {
        showToast('Quote posted')
        setText('')
        onClose()
      } else {
        showToast('Failed to post quote — try again')
      }
    } catch (error) {
      console.error('Quote repost error:', error)
      showToast('Failed to post quote — try again')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50" />

      <div
        className="relative bg-white rounded-2xl w-full max-w-lg mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <button
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-5 py-1.5 rounded-full text-sm font-bold transition-colors ${
              submitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-onrecord-orange text-white hover:bg-onrecord-orange-dark'
            }`}
          >
            {submitting ? 'Posting...' : 'Post'}
          </button>
        </div>

        {/* Composer */}
        <div className="px-4 py-3">
          <div className="flex gap-3">
            <Avatar
              src={currentUser?.avatar}
              alt={currentUser?.name || 'You'}
              size="md"
            />
            <div className="flex-1">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, maxLength))}
                placeholder="Add a comment..."
                className="w-full resize-none text-[15px] leading-5 text-gray-900 placeholder-gray-500 outline-none min-h-[60px]"
                autoFocus
              />
            </div>
          </div>

          {/* Character count */}
          {text.length > 0 && (
            <div className="flex justify-end mt-1">
              <span className={`text-xs ${text.length >= maxLength ? 'text-red-500' : 'text-gray-400'}`}>
                {text.length}/{maxLength}
              </span>
            </div>
          )}

          {/* Embedded post card */}
          <div className="mt-3 border border-gray-200 rounded-2xl overflow-hidden">
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 mb-2">
                <Avatar
                  src={post.author.avatar}
                  alt={post.author.name}
                  size="sm"
                />
                <div className="flex items-center gap-1">
                  <span className="font-bold text-sm text-gray-900">{post.author.name}</span>
                  {post.author.verified && (
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#1DA1F2"/><path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                  <span className="text-sm text-gray-500">@{post.author.username}</span>
                </div>
              </div>
              <p className="text-sm text-gray-800 line-clamp-3">{post.content}</p>
              {post.onRecordCode && (
                <p className="text-xs font-mono text-gray-400 mt-1">{post.onRecordCode}</p>
              )}
            </div>
            {post.mediaUrl && (
              <div className="border-t border-gray-200">
                {post.mediaType === 'video' ? (
                  <video src={post.mediaUrl} className="w-full max-h-[200px] object-cover" muted />
                ) : (
                  <img src={post.mediaUrl} alt="" className="w-full max-h-[200px] object-cover" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
